import { ENUM__html_ref_stub } from '@/enums/ENUM__html_ref_stub';
import { assert_not_null } from '@shared/asserts/utils_asserts';
import { CONSTANTS_GENERATION__value_range } from '@shared/constants/CONSTANTS_GENERATION__value_range';
import { DTO__file_data } from '@shared/dto/+image/image_asset_base/DTO__file_data';
import { DTO__image_asset } from '@shared/dto/+image/image_asset_base/DTO__image_asset';
import { DTO__image_size } from '@shared/dto/+image/image_asset_base/DTO__image_size';
import { timestamp_ms } from '@shared/utils/utils_time';
import { path_relative_to_absolute } from './utils_files_client';

export class UTILS__image {
  static async load_from_url(url_absolute: string): Promise<HTMLImageElement> {
    return new Promise((resolve, reject) => {
      const image: HTMLImageElement = new Image();
      image.crossOrigin = 'anonymous';
      image.onload = () => resolve(image);
      image.onerror = reject;
      image.src = url_absolute;
    });
  }
  static async load_for_asset(asset_image: DTO__image_asset): Promise<HTMLImageElement> {
    if (asset_image.is_stub()) return ENUM__html_ref_stub.IMAGE;

    let url_absolute = path_relative_to_absolute(asset_image.url_relative_latest);

    return this.load_from_url(url_absolute);
  }
}

export async function create_single_color_image(color: string, image_size: DTO__image_size) {
  const canvas = new OffscreenCanvas(image_size.width, image_size.height);

  const ctx = canvas.getContext('2d');
  assert_not_null(ctx);

  ctx.fillStyle = color;
  ctx.fillRect(0, 0, image_size.width, image_size.height);

  let out = await file_data_from_canvas_offscreen(canvas);

  return out;
}

export async function try_downscale_image_file(file: File): Promise<File> {
  const bitmap: ImageBitmap = await createImageBitmap(file);

  const { width: width_original, height: height_original } = bitmap;

  let image_side_max = CONSTANTS_GENERATION__value_range.IMAGE_SIZE_IN.max;

  const scale: number = Math.min(1, image_side_max / Math.max(width_original, height_original));

  if (scale === 1) {
    bitmap.close();
    return file;
  }

  const width_target: number = Math.round(width_original * scale);
  const height_target: number = Math.round(height_original * scale);

  const canvas: HTMLCanvasElement = document.createElement('canvas');
  canvas.width = width_target;
  canvas.height = height_target;

  const ctx: CanvasRenderingContext2D | null = canvas.getContext('2d');
  assert_not_null(ctx);

  ctx.imageSmoothingEnabled = false;
  ctx.drawImage(bitmap, 0, 0, width_target, height_target);
  bitmap.close();

  const blob: Blob = await new Promise(resolve => canvas.toBlob(b => resolve(b as Blob), file.type));

  return new File([blob], file.name, { type: file.type, lastModified: timestamp_ms() });
}

export async function convert_file_to_webp(file: File): Promise<File> {
  const bitmap: ImageBitmap = await createImageBitmap(file);

  const canvas: HTMLCanvasElement = document.createElement('canvas');
  [canvas.width, canvas.height] = [bitmap.width, bitmap.height];

  const ctx: CanvasRenderingContext2D | null = canvas.getContext('2d');
  assert_not_null(ctx);

  ctx.drawImage(bitmap, 0, 0);
  bitmap.close();

  const blob: Blob = await new Promise(resolve => canvas.toBlob(b => resolve(b as Blob), 'image/webp'));

  return new File([blob], file.name.replace(/\.\w+$/, '.webp'), { type: 'image/webp', lastModified: timestamp_ms() });
}

export async function file_data_from_canvas_offscreen(canvas: OffscreenCanvas) {
  const blob = await canvas.convertToBlob({ type: 'image/webp' });
  let out = new DTO__file_data();

  const buffer = await blob.arrayBuffer();

  out.blob = new Uint8Array(buffer);
  out.filename = 'image.webp';
  out.mimetype = 'image/webp';
  out.size = blob.size;

  return out;
}
